/**
 * Route paths for the Test section, shared by the nav config, the redirects
 * and every in-page navigation. The router's own match segments live in App.tsx.
 */

export const TEST_PREFIX = '/test';
/** The Playground: a chat with Kim kept in Kim AI itself. */
export const PLAYGROUND_PATH = '/test/playground';
export const SIMULATOR_PATH = '/test/simulator';
/** A kept simulator run, reopened as it ended. */
export const simulatorRunUrl = (requestId: string): string =>
	`${SIMULATOR_PATH}?run=${encodeURIComponent(requestId)}`;
export const BACK_TESTING_PATH = '/test/back-testing';
export const BACK_TESTING_BULK_RUN_PATH = '/test/back-testing/bulk-run';

export const backTestingUrl = (sopId: string, typeId: string): string =>
	`${BACK_TESTING_PATH}?sop=${sopId}&type=${typeId}`;

/**
 * The scenario list of one type, with a bulk run's picks ticked when one is named. Only the run's id
 * travels: the server holds what it ran, and the link survives a reload.
 */
export const reselectScenariosUrl = (sopId: string, typeId: string, runId?: string): string =>
	`${BACK_TESTING_PATH}?sop=${sopId}&type=${typeId}&view=scenarios${runId ? `&reselect=${runId}` : ''}`;

/** The scenario list of a dataset's type, picking more of its scenarios to add to the dataset as cases. */
export const pickCasesUrl = (sopId: string, typeId: string, datasetId: string): string =>
	`${reselectScenariosUrl(sopId, typeId)}&dataset=${datasetId}`;

/** A type's bulk run: the one named, else the latest. */
export const bulkRunUrl = (sopId: string, typeId: string, runId?: string): string =>
	`${BACK_TESTING_BULK_RUN_PATH}?sop=${sopId}&type=${typeId}${runId ? `&run=${runId}` : ''}`;

/**
 * A protocol with its Promote scenarios drawer open: the one place the promotion pipeline runs, from
 * scenario types to promoting. A type named opens on it.
 */
export const protocolPromoteUrl = (protocolId: string, typeId?: string): string =>
	`/training/protocols/${protocolId}?promote=1${typeId ? `&type=${typeId}` : ''}`;

/** Datasets have pages of their own, outside back testing. */
export const DATASETS_PATH = '/test/datasets';

/** A dataset on the run named, else on its latest. */
export const datasetUrl = (datasetId: string, runId?: string): string =>
	`${DATASETS_PATH}/${datasetId}${runId ? `?run=${runId}` : ''}`;

/**
 * One scenario in the simulator, as a page of its own. The id is the whole link:
 * a scenario resolves on its own and says which SOP it sits under, so this
 * survives a reload, works from history and can be given to someone else,
 * without a second tab sharing anything with the first.
 */
export const scenarioRunUrl = (scenarioId: string): string => `${SIMULATOR_PATH}?scenario=${scenarioId}`;

/** A source with its Promote knowledge drawer open: generating, checking and promoting its answers. */
export const sourcePromoteUrl = (sourceId: string): string => `/training/sources/${sourceId}?promote=1`;
